"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useEditFuturesOrder = void 0;
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const trade_utils_lib_1 = require("trade_utils_lib");
const interface_1 = require("../../../../interface");
const errors_1 = require("../../../../interface/errors");
const CalculateUtils_1 = require("../CalculateUtils");
const FutureTradeProcessor_1 = require("../FutureTradeProcessor");
const FutureAPI_1 = require("../../../../apis/FutureAPI");
const component_1 = require("./../../../../component");
/**
 * 编辑市价单，限价单对应的hooks
 */
function useEditFuturesOrder(params) {
    const { order, isActive = true, leverage = 5, symbolInfo, curPrice, marginType } = params;
    const { type, price, qty, side, position_side } = order;
    const { positionMode } = (0, component_1.usePositionMode)(trade_utils_lib_1.AccountTradeType.trader, isActive);
    // 是否是合约限价单
    const isLimit = CalculateUtils_1.CalculateUtils.isFuturesLimit(type);
    const orderTypeUI = CalculateUtils_1.CalculateUtils.mapOrderTypeToUI(type);
    // const postOnly = type === CustomOrderType.POST;
    const reduceOnly = CalculateUtils_1.CalculateUtils.isOrderReduceOnly(side, position_side);
    // 限价单对应的价格
    const [orderPrice, setOrderPrice] = (0, react_1.useState)(new trade_utils_lib_1.SafeDecimal(price).toFixed() || "");
    // 订单对应的下单量
    const [orderQty, setOrderQty] = (0, react_1.useState)(qty);
    const orderMargin = (0, react_1.useMemo)(() => {
        if (orderTypeUI !== interface_1.FuturesOrderTypeUI.limit) {
            return "";
        }
        return new trade_utils_lib_1.SafeDecimal(qty).abs().mul(new trade_utils_lib_1.SafeDecimal(price)).div(leverage).toString();
    }, [leverage, orderTypeUI, price, qty]);
    const { createOrder, verifyCanTrade: verifyOrder, availableMargin, } = (0, FutureTradeProcessor_1.useFutureTradeParams)({
        symbolInfo,
        marginType,
        orderTypeUI,
        orderSide: side,
        reduceOnly,
        postOnly: false,
        leverage: leverage !== null && leverage !== void 0 ? leverage : 5,
        investSide: interface_1.InvestSymbolSide.base,
        price: orderPrice,
        qty: orderQty,
        curPrice,
        isFocused: isActive,
        releaseOrderMargin: orderMargin,
        positionMode,
    });
    // 只有在编辑限价单才需要进行保证金校验逻辑，市价单跟条件单都不需要进行保证金校验
    /**
     * 增加保证金校验逻辑
     * 1. 确定当前限价单的占用保证金 A
     * 2. 获取用户修改订单后的占用保证金 B
     * 3. 比较 B 与 A + 当前可用
     * 如果B > A + 当前可用，修改订单失败， 提醒需要更多的保证金
     * 如果 B<= A + 当前可用， 修改成功
     */
    const checkAvailableEnough = (0, react_1.useCallback)(() => {
        // 获取当前占用的保证金
        const currentMargin = new trade_utils_lib_1.SafeDecimal(qty)
            .abs()
            .mul(price || "")
            .div(leverage);
        const newMargin = new trade_utils_lib_1.SafeDecimal(orderQty).abs().mul(new trade_utils_lib_1.SafeDecimal(orderPrice)).div(new trade_utils_lib_1.SafeDecimal(leverage));
        if (currentMargin.add(new trade_utils_lib_1.SafeDecimal(availableMargin)).gte(new trade_utils_lib_1.SafeDecimal(newMargin))) {
            return (0, rxjs_1.of)(true);
        }
        // 保证金不够
        return (0, rxjs_1.throwError)(errors_1.TRADE_FUTURES_AVAILABLE_NOT_ENOUGH);
    }, [availableMargin, leverage, orderPrice, orderQty, price, qty]);
    const verifyCanTrade = (0, react_1.useCallback)(() => {
        // 非限价单或者reduceOnly单不需要检测保证金是否可用
        if (!isLimit || reduceOnly) {
            return verifyOrder();
        }
        return checkAvailableEnough().pipe((0, operators_1.mergeMap)(verifyOrder), (0, operators_1.catchError)((error) => {
            return (0, rxjs_1.throwError)((0, trade_utils_lib_1.getTradeDependencyInstance)().localizeError(error));
        }));
    }, [checkAvailableEnough, isLimit, reduceOnly, verifyOrder]);
    const checkUserModify = (0, react_1.useCallback)(() => {
        return !new trade_utils_lib_1.SafeDecimal(orderQty).eq(new trade_utils_lib_1.SafeDecimal(qty)) || !new trade_utils_lib_1.SafeDecimal(orderPrice).eq(new trade_utils_lib_1.SafeDecimal(price));
    }, [orderPrice, orderQty, price, qty]);
    const modifyOrder = (0, react_1.useCallback)(() => {
        return verifyCanTrade().pipe((0, operators_1.mergeMap)(({ request }) => (0, FutureAPI_1.modifyCustomOrder)(Object.assign({ order_id: order.id }, request))), (0, operators_1.catchError)((error) => {
            return (0, rxjs_1.throwError)((0, trade_utils_lib_1.getTradeDependencyInstance)().localizeError(error));
        }));
    }, [order.id, verifyCanTrade]);
    return {
        createOrder,
        setOrderPrice,
        setOrderQty,
        orderPrice,
        orderQty,
        verifyCanTrade,
        checkUserModify,
        modifyOrder,
    };
}
exports.useEditFuturesOrder = useEditFuturesOrder;
